import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { APP_MESSAGES, GATEWAY_TYPES } from 'src/shared/config/src';
import { getCurrentChild } from 'src/shared/redux-state/src';
import { GlobalErrorActions } from 'src/shared/redux-state/src/lib/globalError/GlobalErrorSlice';
import { payAmount } from 'src/shared/services/src/lib/invoice';
import { ChildrenStoreData, ErrorType } from 'src/shared/types/src';
import { useAppDispatch, useAppSelector } from '../../services/store';
import LocalImages from '../../static';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { CustomButton, CustomTextInput } from 'src/app/components';
import { replace } from 'lodash';

type PaymentModalType = {
    balance?: string,
    currency?: string,
    type?: string,
    callbackOnSuccess?: Function,
    isPaymentModal: boolean,
    togglePaymentModal: () => void
}

const PaymentModal: React.FC<PaymentModalType> = ({ balance, currency, type, callbackOnSuccess, isPaymentModal, togglePaymentModal }) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate()
    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

    // const webViewRef = useRef<WebView>(null);

    const FIELD_MAP = {
        AMOUNT: 'amount',
        DEPOSIT: 'deposit'
    };

    const apiErrorMap: { [key: string]: string } = {
        "amount": FIELD_MAP.AMOUNT,
        "deposit": FIELD_MAP.DEPOSIT
    };

    const [errors, setErrors] = useState<ErrorType>({});
    const [total, setTotal] = useState<any>('0.00');
    const [amount, setAmount] = useState<any>('0.00');
    const [deposit, setDeposit] = useState<any>('0.00');
    const [loading, setLoading] = useState<boolean>(false);


    const payBalanceAmount = async () => {
        try {
            setLoading(true);
            const response = await payAmount({ child: currentChild, amount, deposit, total });

            if (response) {
                togglePaymentModal();
                window.open(response?.data?.paymentUrl, '_blank', 'noopener,noreferrer');

            } else {
                if (Object.keys(response?.errors).length > 0) {
                    const errorMessage = Object.values(response?.errors)[0] as string;
                    dispatch(GlobalErrorActions.setError({ isError: true, message: errorMessage }));
                } else {
                    dispatch(GlobalErrorActions.setError({ isError: true, message: APP_MESSAGES.generalErrorMessage }));
                }
            }

        } catch (error) {
            if (error instanceof AxiosError) {
                if (error && error?.response) {
                    const { errors, validation_errors } = error?.response?.data;
                    let errorObj: ErrorType = {};
                    if (validation_errors && Object.keys(validation_errors).length > 0) {
                        for (let [key, value] of Object.entries(validation_errors)) {
                            errorObj = {
                                ...errorObj,
                                [apiErrorMap[key]]: {
                                    isErrored: true,
                                    errorMsg: (value as any[]).length > 1 ? (value as any[]).join("\n") : (value as any[])[0]
                                }
                            }
                        }
                        setErrors(errorObj);
                    } else if (errors && Object.keys(errors).length > 0) {
                        const errorMessage = Object.values(errors)[0] as string;
                        dispatch(GlobalErrorActions.setError({ isError: true, message: errorMessage }));
                    } else {
                        dispatch(GlobalErrorActions.setError({ isError: true, message: APP_MESSAGES.generalErrorMessage }));
                    };
                } else {
                    dispatch(GlobalErrorActions.setError({ isError: true, message: error?.message }));
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Number(balance) > 0) {
            setAmount(balance)
        }
    }, [balance]);

    useEffect(() => {
        let total = 0;

        if (!isNaN(Number(amount))) {
            total = total + Number(amount)
        };

        if (!isNaN(Number(deposit))) {
            total = total + Number(deposit)
        };

        setTotal(total.toFixed(2))

    }, [amount, deposit]);

    const handleAmountChange = (text: string) => {
        if (errors && errors[FIELD_MAP.AMOUNT] && errors[FIELD_MAP.AMOUNT].isErrored) {
            delete errors[FIELD_MAP.AMOUNT]
        };
        setAmount(text.replace(/[^0-9.]/g, ''))
    };

    const handleDepositChange = (text: string) => {
        setDeposit(text.replace(/[^0-9.]/g, ''))
    };

    const getGatewayImage = () => {
        if (type === GATEWAY_TYPES.STRIPE) {
            return LocalImages.stripeIcon
        };

        if (type === GATEWAY_TYPES.OPAYO) {
            return LocalImages?.opayoIcon
        };

        return LocalImages.worldpayIcon;
    };

    const handleOnMessageChange = (e: any) => {
        if (e?.nativeEvent?.data === "CLOSE") {
            // setShowWebView(false);
            if (callbackOnSuccess) callbackOnSuccess()
        };
    };

    return (
        <Modal 
            isOpen={isPaymentModal} 
            toggle={togglePaymentModal} 
            className={`custom-modal-md modal-dialog-bottom modal-dialog-bottom-style`}
            modalClassName={`modal-slide-down`}
            backdrop={"static"} 
            keyboard={false} 
            centered={true}
        >
            <ModalHeader toggle={togglePaymentModal}>Pay Now</ModalHeader>
            <ModalBody>
                <div className='d-flex align-items-center justify-content-center mg-b-15'>
                    <div className='payment-modal-logo'><img src={getGatewayImage()} alt="gate-way-img" /></div>
                </div>

                <div className='mg-t-30'>
                    <div className='d-flex pd-x-20'>
                        <div className='fs-12 text-gray-300 mg-r-45 d-flex align-items-center'  style={{ height: '40px' }}>Amount:</div>
                        <div className='flex-1'>
                            <CustomTextInput
                                type="text"
                                name='amount'
                                value={Number(amount) > 0 ? amount : 0}
                                onChange={(e) => handleAmountChange(e.target.value)}
                                onBlur={() => {
                                    setAmount((val: any) => isNaN(Number(val)) ? '0.00' : Number(val).toFixed(2))
                                }}
                                icon={currency}
                                iconPosition='left'
                                customIconStyle={'fw-bold text-gray-300 opacity-75'}
                            />
                            {errors && errors[FIELD_MAP.AMOUNT] && errors[FIELD_MAP.AMOUNT].isErrored ? <span className="form-error">{errors[FIELD_MAP.AMOUNT].errorMsg}</span> : null}
                        </div>
                    </div>
                </div>


                <div className='mg-t-30'>
                    <div className='d-flex pd-x-20'>
                        <div className='fs-12 text-gray-300 mg-r-45 d-flex align-items-center' style={{ height: '40px' }}>Deposit:</div>
                        <div className='flex-1'>
                            <CustomTextInput
                                type="text"
                                name='amount'
                                value={Number(deposit) > 0 ? deposit : 0}
                                onChange={(e) => handleDepositChange(e.target.value)}
                                onBlur={() => {
                                    setAmount((val: any) => isNaN(Number(val)) ? '0.00' : Number(val).toFixed(2))
                                }}
                                icon={currency}
                                iconPosition='left'
                                customIconStyle={'fw-bold text-gray-300 opacity-75'}
                            />
                             {errors && errors[FIELD_MAP.DEPOSIT] && errors[FIELD_MAP.DEPOSIT].isErrored ? <span className="form-error">{errors[FIELD_MAP.DEPOSIT].errorMsg}</span> : null}
                        </div>
                    </div>
                </div>

                <div className='pd-t-15 mg-t-30 border-top border-gray-200'>
                    <div className='d-flex align-items-center justify-content-between pd-x-20'>
                        <div className='fs-14 text-black fw-semibold mg-r-45'>Total:</div>
                        <div className='fs-14 text-black fw-semibold flex-1 pd-l-20'>{currency} {total}</div>
                    </div>
                </div>

            </ModalBody>
            <ModalFooter>
                <CustomButton text="Proceed" color="primary" disabled={loading || Number(total) == 0} isLoading={loading} onClick={() => {
                    if (Number(total) > 0) {
                        payBalanceAmount()
                    }
                }}
                />
            </ModalFooter>
        </Modal>
    )
};

export default PaymentModal;